import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  PlanificadoresKeyActionsEnum,
  useBlockManyHours,
  useUnblockManyHours,
} from '@/shared/actions/erp';
import { CustomTextArea, SingleButton } from '@/shared/components/ui';
import { ScrollableDialogProps } from '@/shared/components/ui/Dialogs';
import { permaBlockTimeSchema } from '@/shared/utils/validation-schema';
import { useAuthStore } from '@/store/auth';
import { usePlanificadorStore } from '@/store/erp';
import { useUiStore } from '@/store/ui';
import { PermanentBlockTimeForm } from '@/shared/interfaces/erp/mantenimiento-operaciones';
import { ROUTER_PATHS } from '@/shared';

export type MultipleSlotsModalProps = {
  open: boolean;
  onClose: () => void;
  planificadorId: string;
};

type PermaBlockForm = Pick<PermanentBlockTimeForm, 'motivo'> & {
  isUnblockRequest?: boolean;
};

const MultipleSlotsModal: React.FC<MultipleSlotsModalProps> = ({
  open,
  planificadorId,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const returnUrlPlanificadoresPage =
    ROUTER_PATHS.mantenimientoOperaciones.planificadorNav;
  ///* global state -----------------
  const [eventHoursArray, setEventHoursArray] = useState<number[]>([])
  const [isUnblockRequest, setRequestUnblock] = useState<boolean>(false)

  ///* global state -----------------
  const selectedEventsArray = usePlanificadorStore(s => s.selectedEventsArray);
  const handleRefreshPlanificador = usePlanificadorStore(s => s.handleRefreshPlanificador);

  const user_id = useAuthStore(s => s.user?.user_id);
  const setIsGlobalLoading = useUiStore(s => s.setIsGlobalLoading);
  const setIsFetchingEvents = usePlanificadorStore(s => s.setIsFetchingEvents);
  const setSelectedEventsArray = usePlanificadorStore(
    s => s.setSelectedEventsArray
  );
  const selectedPlanificador = usePlanificadorStore(
    s => s.selectedPlanificador
  );

  ///* form -----------------
  const form = useForm<PermaBlockForm>({
    resolver: yupResolver(permaBlockTimeSchema),
  });
  const { formState } = form;

  const customErrorCallback = () => {
    // 'caise error not re-render calendar
    //window.location.href = `${returnUrlPlanificadoresPage}/horario/${planificadorId}`;
  };

  ///* mutations -----------------
  const blockManyHours = useBlockManyHours({
    enableRedirect: false,
    customErrorCallback,
  })

  const unblockManyHours = useUnblockManyHours({
    redirect: false,
    customErrorCallback,
  })

  ///* handlers -----------------
  const onSubmit = async (data: PermaBlockForm) => {
    try {
      setIsGlobalLoading(true);
      setIsFetchingEvents(true);
      onClose();

      // unblock time
      if (isUnblockRequest) {
        const resp = await unblockManyHours.mutateAsync({
          motivo: data.motivo,
          fecha: dayjs(selectedEventsArray[0]?.start).format('YYYY-MM-DD'),
          horas: eventHoursArray,
          user_id: user_id!,
          flota: selectedPlanificador?.flota_data?.codigo!,
        });

        await queryClient.invalidateQueries({
          queryKey: [PlanificadoresKeyActionsEnum.PLANIFICADOR, planificadorId],
        });

        handleClose();
        handleRefreshPlanificador(resp.data);

        return;
      }

      // block time
      const resp = await blockManyHours.mutateAsync({
        motivo: data.motivo,
        fecha: dayjs(selectedEventsArray[0]?.start).format('YYYY-MM-DD'),
        horas: eventHoursArray,
        user_id: user_id!,
        flota: selectedPlanificador?.flota_data?.codigo!,
        forzar: true,
      });

      handleRefreshPlanificador(resp.data);

      await queryClient.invalidateQueries({
        queryKey: [PlanificadoresKeyActionsEnum.PLANIFICADOR, planificadorId],
      });

      handleClose();
    } finally {
      setIsGlobalLoading(false);
      setIsFetchingEvents(false);
    }
  };

  const handleClose = () => {
    onClose();
    form.reset();
    form.setValue('motivo', '');
    setEventHoursArray([]);
    setSelectedEventsArray([]);
  };

  ///* effects -----------------

  useEffect(() => {
    if (!open) return;

    // get hours from selected events
    const eventHours: number[] = selectedEventsArray.map(event => {
      return event.hora;
    });

    if (selectedEventsArray.length === 1) {
      form.setValue('motivo', selectedEventsArray.at(0)?.motivo)
    }

    setEventHoursArray(eventHours);

    // is necessary unblock only if all selected events are blocked
    const isAllBlocked = selectedEventsArray.every(event => event.estado);
    setRequestUnblock(isAllBlocked);
    form.setValue('isUnblockRequest', !isAllBlocked);
  }, [open]);

  return (
    <>
      <ScrollableDialogProps
        open={open}
        title={isUnblockRequest ? 'Desbloquear Horario' : 'Bloquear Horario'}
        minWidth="30%"
        onClose={handleClose}

        // custom action buttons
        showCustomButtons
        customButtons={
          <>
            <SingleButton
              label={'Cancelar'}
              onClick={handleClose}
              variant="text"
              disabled={false}
            />

            <SingleButton
              label={isUnblockRequest ? 'Desbloquear' : 'Bloquear'}
              onClick={form.handleSubmit(onSubmit, () => { })}
              variant="outlined"
              disabled={formState.isSubmitting}
            />
          </>
        }
        // content
        contentNode={
          <Grid container spacing={3} pt={2} pb={3}>
            <Grid item xs={12} pb={1}>
              <Typography variant="body1">
                Se {isUnblockRequest ? 'desbloquearán' : 'bloquearán'} los
                siguientes horarios para el día{' '}
                <b>
                  {' '}
                  {dayjs(selectedEventsArray[0]?.start).format('DD/MM/YYYY')}
                </b>
                :
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {dayjs(selectedEventsArray[0]?.start).format('HH:mm')} -{' '}
                {dayjs(selectedEventsArray?.at(-1)?.end).format('HH:mm')}
              </Typography>
            </Grid>

            <CustomTextArea
              label="Motivo"
              name="motivo"
              required
              rows={4}
              control={form.control}
              defaultValue={form.getValues().motivo}
              error={formState.errors.motivo}
              helperText={formState.errors.motivo?.message}
            //disabled={formState.isSubmitting || isUnblockRequest}
            />
          </Grid>
        }
      />
    </>
  );
};

export default MultipleSlotsModal;
